import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ICONS as Icons } from "../../data/iconRegistry";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { NETWORKS_META } from "../../data/networksMeta";
import { Button } from "../ui/button";
import HomeComboCarousel from "./HomeComboCarousel";

const LOGO_LAYOUT = [
  { slug: "instagram", top: "10%", left: "8%", size: 56, rotate: -10, opacity: "text-white/[0.16]" },
  { slug: "tiktok", top: "12%", left: "84%", size: 48, rotate: 12, opacity: "text-white/[0.18]" },
  { slug: "facebook", top: "34%", left: "4%", size: 40, rotate: 6, opacity: "text-white/[0.14]" },
  { slug: "youtube", top: "66%", left: "6%", size: 50, rotate: -8, opacity: "text-white/[0.18]" },
  { slug: "twitter", top: "6%", left: "42%", size: 32, rotate: 4, opacity: "text-white/[0.14]" },
  { slug: "snapchat", top: "86%", left: "22%", size: 44, rotate: -12, opacity: "text-white/[0.16]" },
  { slug: "telegram", top: "22%", left: "66%", size: 36, rotate: -6, opacity: "text-white/[0.15]" },
  { slug: "linkedin", top: "40%", left: "88%", size: 32, rotate: -10, opacity: "text-white/[0.14]" },
  { slug: "twitch", top: "88%", left: "76%", size: 42, rotate: 10, opacity: "text-white/[0.16]" },
  { slug: "threads", top: "4%", left: "63%", size: 30, rotate: 6, opacity: "text-white/[0.14]" },
  { slug: "discord", top: "56%", left: "10%", size: 34, rotate: 14, opacity: "text-white/[0.15]" },
  { slug: "soundcloud", top: "20%", left: "22%", size: 28, rotate: 5, opacity: "text-white/[0.13]" },
];

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden pt-20 pb-28 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-10%] left-[10%] w-[500px] h-[500px] rounded-full bg-indigo-600/25 blur-[120px] animate-drift" />
        <div className="absolute bottom-[-10%] right-[10%] w-[500px] h-[500px] rounded-full bg-fuchsia-600/20 blur-[120px] animate-drift" style={{ animationDelay: "3s" }} />
        <div className="absolute inset-0" data-testid="hero-networks-backdrop">
          {LOGO_LAYOUT.map((item, i) => {
            const meta = NETWORKS_META[item.slug];
            const Icon = Icons[meta.icon] || Icons.Globe;
            return (
              <div
                key={item.slug}
                className="absolute"
                style={{ top: item.top, left: item.left, transform: "translate(-50%, -50%)" }}
              >
                <motion.div
                  className={item.opacity}
                  initial={{ opacity: 0, rotate: item.rotate }}
                  animate={{ opacity: 1, rotate: item.rotate, y: [0, -14, 0] }}
                  transition={{
                    opacity: { duration: 1, delay: 0.05 * i },
                    y: { duration: 5 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: (i % 5) * 0.4 },
                  }}
                  data-testid={`hero-network-logo-${item.slug}`}
                >
                  <Icon style={{ width: item.size, height: item.size }} strokeWidth={1.5} />
                </motion.div>
              </div>
            );
          })}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      <div className="mx-auto max-w-5xl text-center relative">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs text-indigo-300 mb-8">
          <Sparkles className="w-3.5 h-3.5" /> {t("hero.orders_badge", "+250 000 commandes livrées dans le monde")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6"
          data-testid="hero-title"
        >
          <span className="text-white">{t("hero.title", "Boostez votre présence sur les réseaux sociaux")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto mb-6"
          data-testid="hero-subtitle"
        >
          {t("hero.subtitle", "Followers, Likes, Views et engagement réel pour développer votre communauté.")}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
          className="text-sm text-zinc-500 max-w-3xl mx-auto mb-10"
          data-testid="hero-brand-intro"
        >
          {t("home.brand_intro", "Buy Likes and Followers est une plateforme de croissance des réseaux sociaux qui aide créateurs, marques et influenceurs à développer leur visibilité en ligne.")}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}>
          <HomeComboCarousel />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/reseaux/instagram">
            <Button size="lg" className="rounded-full bg-indigo-500 hover:bg-indigo-600 hover:-translate-y-0.5 transition-transform px-8" data-testid="hero-cta-buy">
              {t("hero.cta_buy", "Acheter maintenant")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <a href="#networks">
            <Button size="lg" variant="outline" className="rounded-full border-white/15 hover:bg-white/5 px-8" data-testid="hero-cta-services">
              {t("hero.cta_services", "Voir les services")}
            </Button>
          </a>
        </motion.div>

        <div className="mt-20 grid grid-cols-3 gap-6 max-w-3xl mx-auto">
          {[
            { label: t("hero.stat1_label", "Followers actifs"), value: "24/7" },
            { label: t("hero.stat2_label", "Note moyenne"), value: "4.9/5" },
            { label: t("hero.stat3_label", "Livraison"), value: "24-72h" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
              className="glass rounded-2xl p-5 animate-float" style={{ animationDelay: `${i * 0.6}s` }}
            >
              <div className="font-heading text-2xl font-bold text-white">{s.value}</div>
              <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
